import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { getMinimalCartEntryType } from 'features/cart/utils';

import { ILegacyCartEntry, ISanityImage } from '@rbi-ctg/menu';
import {
  CartServiceMode,
  EntryTypeEnum,
  IConfigOfferEntryDto,
  IHydrateCartMutation,
  IMenuEntryDto,
  IMenuSelectionInput,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  ServiceMode,
} from 'generated/graphql-gateway';
import { ModifiersSelections, SlotsSelections } from 'pages/menu/components/product-wizard/types';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import {
  getEntryPriceOrZero,
  isCombo,
  isItem,
  isModifier,
  mapImageResourceToSanityImage,
} from 'state/unified-menu/utils';
import { CartEntryType } from 'utils/cart/types';

const REWARD_COMBO_MARKER = 'loyalty';

export const getCartEntryId = ({ _id, sanityId }: ILegacyCartEntry): string =>
  sanityId?.trim().length ? sanityId : _id;

export function buildMinimalCartEntriesLookup(
  minimalCartEntries: ICartEntry[]
): Map<string, ICartEntry> {
  const minimalCartEntriesLookup = new Map<string, ICartEntry>();

  function addEntriesToLookup(entries: ICartEntry[]) {
    entries.forEach(entry => {
      minimalCartEntriesLookup.set(entry.id, entry);
      if (entry.children && entry.children.length > 0) {
        addEntriesToLookup(entry.children);
      }
    });
  }

  addEntriesToLookup(minimalCartEntries);

  return minimalCartEntriesLookup;
}

/**
 * This function is used to find a minimal cart entry by sanityId.
 * It first tries to find a direct match, and if not found,
 * it searches through all entries to find one with matching menuSelections.
 */

export function findMinimalCartEntryBySanityId(
  /**
   * The lookup of minimal cart entries.
   */
  minimalCartEntriesLookup: Map<string, ICartEntry>,
  sanityId: string
): ICartEntry | undefined {
  // First try direct lookup
  const directMatch = minimalCartEntriesLookup.get(sanityId);
  if (directMatch) {
    return directMatch;
  }

  // If not found, search through all entries to find one with matching menuSelections
  for (const [, minimalCartEntry] of minimalCartEntriesLookup.entries()) {
    // Check if any menuSelection has the matching sanityId
    const hasMatchingMenuSelection = minimalCartEntry.menuSelections?.some(
      selection => selection.id === sanityId
    );

    if (hasMatchingMenuSelection) {
      return minimalCartEntry;
    }
  }

  return undefined;
}

export function findMatchingCartEntry(
  minimalCartEntries: ICartEntry[],
  cartEntry: ILegacyCartEntry
): ICartEntry | undefined {
  return minimalCartEntries.find(
    x => x.cartId === cartEntry.cartId || x.id === cartEntry.sanityId || x.id === cartEntry._id
  );
}

export function findRewardBenefitFromHydratedCartEntry(
  cartEntry: ILegacyCartEntry
): ILegacyCartEntry | undefined {
  if (!isHydratedReward(cartEntry)) {
    return undefined;
  }

  const recursiveFind = (cartEntries: ILegacyCartEntry[] = []): ILegacyCartEntry | undefined => {
    for (const child of cartEntries) {
      if (child.cartId !== REWARD_COMBO_MARKER) {
        return child;
      }

      const foundBenefit = recursiveFind(child.children);
      if (foundBenefit) {
        return foundBenefit;
      }
    }

    return undefined;
  };

  return recursiveFind(cartEntry.children);
}

export function findModifierPrice(
  modifier: ILocalizedMenuEntry,
  parentMenuEntry: IMenuEntryDto,
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    posServiceMode: 'delivery' | 'pickup';
  }
): number {
  const modifierRef = parentMenuEntry.options?.entries?.find(entry =>
    modifier.id.includes(entry.entryId)
  );

  const modifierRefPrice = modifierRef?.price?.[options.posServiceMode] ?? 0;

  if (modifierRefPrice > 0) {
    return modifierRefPrice;
  }

  const modifierGroupIds = parentMenuEntry.options?.entries?.map(entry => entry.entryId) ?? [];
  for (const modifierGroupId of modifierGroupIds) {
    const modifierGroup = options.menu.get(modifierGroupId);
    const modifierGroupEntry = modifierGroup.options?.entries?.find(
      entry => entry.entryId === modifier.id
    );

    if (modifierGroupEntry) {
      const modifierGroupPrice = modifierGroupEntry.price?.[options.posServiceMode] ?? 0;
      return modifierGroupPrice;
    }
  }

  const modifierPrice = modifier.price?.[options.posServiceMode] ?? 0;
  return modifierPrice;
}

export function findComboSlotPrice(
  comboSlotMenuEntry: IMenuEntryDto,
  parentMenuEntry: IMenuEntryDto,
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    posServiceMode: 'delivery' | 'pickup';
  }
): number {
  const comboSlotsIds = parentMenuEntry.options?.entries?.map(entry => entry.entryId) ?? [];

  for (const comboSlotId of comboSlotsIds) {
    const comboSlot = options.menu.get(comboSlotId);
    const comboSlotEntry = comboSlot.options?.entries?.find(
      entry => entry.entryId === comboSlotMenuEntry.id
    );

    if (comboSlotEntry) {
      const comboSlotPrice = comboSlotEntry.price?.[options.posServiceMode] ?? 0;
      return comboSlotPrice;
    }
  }

  return 0;
}

export function isDummyItem(item: { name: string } | string): boolean {
  const name = typeof item === 'string' ? item : item.name;
  return ['combo sides', 'combo item', 'mix n match', "mix n' match"].includes(
    name?.toLowerCase().trim()
  );
}

// #region Mapping utils
export function mapLegacyToMinimalCartEntries(legacyCartEntries: ILegacyCartEntry[]): ICartEntry[] {
  return legacyCartEntries.map(legacyCartEntry => {
    const processedChildren = mapLegacyToMinimalCartEntries(legacyCartEntry.children);
    const processedMenuSelections = mapLegacyToMenuSelections(legacyCartEntry.children);

    return {
      cartId: legacyCartEntry.cartId,
      lineId: legacyCartEntry.cartId,
      id: getCartEntryId(legacyCartEntry),
      quantity: legacyCartEntry.quantity,
      name: legacyCartEntry.name,
      image: legacyCartEntry.image! as ISanityImage,
      url: legacyCartEntry.url,
      menuSelections: processedMenuSelections,
      type: MinimalCartEntryType.item,
      children: processedChildren,
      isExtra: legacyCartEntry.isExtra,
      isDonation: legacyCartEntry.isDonation,
    };
  });
}

export function mapLegacyToMenuSelections(
  legacyCartEntries: ILegacyCartEntry[]
): IMenuSelectionInput[] {
  return legacyCartEntries.flatMap(legacyCartEntry => {
    if (
      legacyCartEntry.type === CartEntryType.itemOption ||
      legacyCartEntry.type === CartEntryType.comboSlot ||
      legacyCartEntry.isDummyItem ||
      isDummyItem(legacyCartEntry)
    ) {
      return mapLegacyToMenuSelections(legacyCartEntry.children);
    }

    const nestedMenuSelections = mapLegacyToMenuSelections(legacyCartEntry.children);

    return [
      {
        id: getCartEntryId(legacyCartEntry),
        quantity: legacyCartEntry.quantity,
        menuSelections: nestedMenuSelections,
      },
    ];
  });
}

export function mapServiceModeToCartServiceMode(serviceMode: ServiceMode): CartServiceMode {
  // This is the same as a switch statement, becase the two enums are the same
  return (serviceMode as unknown) as CartServiceMode;
}

export function mapSelectedProductToMenuSelections(
  selectedProduct: ILocalizedMenuEntry,
  quantity: number,
  slotsSelections: SlotsSelections
): IMenuSelectionInput[] {
  return [
    {
      id: selectedProduct.id,
      quantity,
      menuSelections: mapSlotsSelectionsToMenuSelections(slotsSelections, isItem(selectedProduct)),
    },
  ];
}

export function mapSlotsSelectionsToMenuSelections(
  slotsSelections: SlotsSelections,
  isItem: boolean
): IMenuSelectionInput[] {
  return Object.values(slotsSelections)
    .filter(slotSelection => !!slotSelection)
    .flatMap(slotSelection => {
      const modifiersSelections = mapModifiersToMenuSelections(slotSelection!.modifiers ?? {});
      return isItem
        ? modifiersSelections
        : [
            {
              id: slotSelection!.itemId,
              quantity: 1,
              menuSelections: modifiersSelections,
            },
          ];
    });
}

export function mapModifiersToMenuSelections(
  modifiersSelections: ModifiersSelections
): IMenuSelectionInput[] {
  return Object.values(modifiersSelections).flatMap(multipliersSelections =>
    Object.entries(multipliersSelections).map(([multiplierKey, quantity]) => ({
      id: multiplierKey,
      quantity,
    }))
  );
}

export function mapChildrenPriceToMenuSelectionsPrice(
  cartEntry: ICartEntry
): IMenuSelectionInput[] {
  if (!cartEntry.menuSelections || !cartEntry.children) {
    return [...(cartEntry.menuSelections || [])];
  }

  // Create a map of ICartEntry
  const cartEntriesMap = buildMinimalCartEntriesLookup(cartEntry.children);

  // Recursively map menuSelections and add prices from children
  function mapMenuSelectionsWithPrices(
    menuSelections: readonly IMenuSelectionInput[]
  ): IMenuSelectionInput[] {
    return menuSelections.map(menuSelection => {
      const childCartEntry = cartEntriesMap.get(menuSelection.id);

      const updatedMenuSelection: IMenuSelectionInput = {
        ...menuSelection,
        ...(!!childCartEntry.price && {
          price: childCartEntry.price,
        }),
        // Recursively process nested menuSelections
        ...(menuSelection.menuSelections &&
          menuSelection.menuSelections.length > 0 && {
            menuSelections: mapMenuSelectionsWithPrices(menuSelection.menuSelections),
          }),
      };

      return updatedMenuSelection;
    });
  }

  return mapMenuSelectionsWithPrices(cartEntry.menuSelections);
}

export function mapMenuSelectionsToICartEntry(
  menuSelections: IMenuSelectionInput[],
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
    indexedRewards: IndexedMenu<IRewardEntryDto>;
    posServiceMode: 'delivery' | 'pickup';
    url: string;
    parentMenuEntry?: IMenuEntryDto;
    rewardId?: string;
  }
) {
  return menuSelections.map(menuSelection => {
    const menuEntry = options.menu.find(menuSelection.id);

    let price = getEntryPriceOrZero(menuEntry, options.posServiceMode);

    if (options.parentMenuEntry && isCombo(options.parentMenuEntry)) {
      price = findComboSlotPrice(menuEntry, options.parentMenuEntry, options);
    }

    if (options.parentMenuEntry && isModifier(menuEntry)) {
      price = findModifierPrice(menuEntry, options.parentMenuEntry, options);
    }

    return {
      ...menuSelection,
      name: menuEntry?.name?.locale ?? '',
      rewardIdReference: menuEntry?.rewardIdReference,
      image: mapImageResourceToSanityImage(
        menuEntry?.image?.resource ?? '',
        menuEntry?.image?.altText?.locale ?? ''
      ),
      price: {
        amount: price,
        currency: 'USD',
      },
      type: getMinimalCartEntryType(menuEntry?.id, {
        indexedMenu: options.menu,
        indexedOffers: options.indexedOffers,
        indexedRewards: options.indexedRewards,
      }),
      children: mapMenuSelectionsToICartEntry(
        (menuSelection.menuSelections as IMenuSelectionInput[]) ?? [],
        { ...options, parentMenuEntry: menuEntry }
      ),
      url: options.url,
    };
  }) as ICartEntry[];
}
/**
 *  @deprecated use mapMenuSelectionsToICartEntry instead
 *  this is still supporting the old structure of the minimal cart entry
 */
export function mapMenuSelectionsToMinimalCartEntry(
  menuSelections: IMenuSelectionInput[],
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
    posServiceMode: 'delivery' | 'pickup';
    url: string;
    rewardId?: string;
    offerId?: string;
  }
): ICartEntry[] {
  return menuSelections.map(menuSelection => {
    const menuEntry = options.menu.find(menuSelection.id);
    const isRewardEntry = menuEntry?.id === options.rewardId;

    const isLoyaltyEntry = !!options.offerId;
    let isOfferPicker = false;
    const offerItem = options.indexedOffers.find(options.offerId);
    if (offerItem) {
      const benefitItemId = offerItem.options?.[0]?.entryId;
      if (benefitItemId) {
        const benefitItem = options.menu.find(benefitItemId);
        if (benefitItem?.type === EntryTypeEnum.PICKER) {
          isOfferPicker = true;
        }
      }
    }

    const children = mapMenuSelectionsToMinimalCartEntry(
      (menuSelection.menuSelections as IMenuSelectionInput[]) ?? [],
      { ...options, offerId: undefined }
    );

    // This is a fallback image intended to be used as the reward image.
    const childrenImage = children.find(c => !!c.image)?.image;

    const image =
      isRewardEntry && childrenImage
        ? childrenImage
        : mapImageResourceToSanityImage(
            menuEntry?.image?.resource ?? '',
            menuEntry?.image?.altText?.locale ?? ''
          );

    /**
     * for most cases including offerCombos menu selections have the same structure as the children
     */
    const output = {
      ...menuSelection,
      ...(isLoyaltyEntry && {
        id: options.offerId,
      }),
      name: menuEntry?.name?.locale ?? '',
      image,
      type: MinimalCartEntryType.item,
      url: options.url,
      children,
      referenceId: menuEntry?.loyaltyEngineIdReference,
    };

    /**
     * note that for offer pickers the menu selections needs to be a little different eg:
      "menuSelections": [
        {
          "id": <id of the offer>,
          "quantity": 1,
          "menuSelections": [
            {
              "id": <id of the combo>,
              "quantity": 1,
              "menuSelections": [
                {
                  "id": <id of the item>,
                  "quantity": 1,
                  "menuSelections": []
                },
              ]
            }
          ]
        }
      ]
     */
    if (isOfferPicker) {
      const comboSelections = output.menuSelections;
      output.menuSelections = [
        {
          id: menuEntry?.id,
          quantity: 1,
          menuSelections: comboSelections,
        },
      ];

      // In order to be able to edit the offer from the cart we need to change the url to the
      // picker entry and add the offerId to the query params
      const pickerEntryUrl = output.url
        .replace('offer', 'picker')
        .replace(options.offerId, menuEntry?.id);
      output.url = `${pickerEntryUrl}?offerId=${options.offerId}`;
      return output;
    }

    return output;
  });
}
// #endregion

// #region Hydration utils
export function isHydratedReward(cartEntry: ILegacyCartEntry): boolean {
  return cartEntry.children.every(child => child.cartId === REWARD_COMBO_MARKER);
}

export function getHydratedEntityId(entryId: string, parentId: string) {
  const prefix = `${parentId}$`;
  // Hydration v2
  if (entryId.startsWith(prefix)) {
    return entryId;
  }
  // Hydration v1
  return `${prefix}${entryId}`;
}

export function mapHydratedCart(
  hydratedCartEntries: IHydrateCartMutation['hydrateCart']['cartEntries'],
  minimalCartEntriesLookup: Map<string, ICartEntry>
): ILegacyCartEntry[] {
  return (hydratedCartEntries ?? []).map(hydratedCartEntry => {
    const minimalCartEntry = findMinimalCartEntryBySanityId(
      minimalCartEntriesLookup,
      hydratedCartEntry.sanityId
    );

    return {
      lineId: minimalCartEntry?.cartId ?? hydratedCartEntry.lineId,
      cartId: minimalCartEntry?.cartId ?? hydratedCartEntry.lineId,
      _id: hydratedCartEntry.sanityId,
      sanityId: hydratedCartEntry.sanityId,
      name: hydratedCartEntry.name,
      type: (hydratedCartEntry.type as unknown) as ILegacyCartEntry['type'],
      quantity: hydratedCartEntry.quantity ?? 1,
      pickerSelections: {},
      posIdsConfiguration: hydratedCartEntry.posIdsConfiguration,
      discountType: hydratedCartEntry.discountType,
      vendorConfigs: hydratedCartEntry.vendorConfigs,
      url: minimalCartEntry?.url ?? '',
      image: minimalCartEntry?.image,
      isExtra: minimalCartEntry?.isExtra,
      isDonation: minimalCartEntry?.isDonation,
      children: mapHydratedCart(
        hydratedCartEntry.children as IHydrateCartMutation['hydrateCart']['cartEntries'],
        minimalCartEntriesLookup
      ),
    } as ILegacyCartEntry;
  });
}

//#endregion

// moved to utils/cart-utils.ts: normalizeCartEntries
